import { Loader2 } from 'lucide-react';

export function PageHeader({ title, description, actions, testid }) {
  return (
    <div
      data-testid={testid || 'page-header'}
      className="flex items-start sm:items-center justify-between gap-3 mb-6"
    >
      <div className="min-w-0">
        <h1 className="page-title truncate">{title}</h1>
        {description ? (
          <p className="text-sm text-muted-foreground mt-1 max-w-xl leading-snug">
            {description}
          </p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex items-center gap-2 shrink-0">{actions}</div>
      ) : null}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, cta, testid }) {
  return (
    <div
      data-testid={testid || 'empty-state'}
      className="surface flex flex-col items-center justify-center py-20 px-6 text-center"
    >
      {Icon ? (
        <div className="mb-4 h-12 w-12 rounded-2xl bg-accent grid place-items-center text-primary">
          <Icon className="h-5 w-5" strokeWidth={1.75} />
        </div>
      ) : null}
      <h3 className="text-base font-semibold tracking-tight">{title}</h3>
      {description ? (
        <p className="text-sm text-muted-foreground mt-1.5 max-w-sm leading-relaxed">
          {description}
        </p>
      ) : null}
      {cta ? <div className="mt-5">{cta}</div> : null}
    </div>
  );
}

export function Stat({ label, value, hint, accent, icon: Icon }) {
  return (
    <div className="kpi-card" data-testid={`stat-${String(label).replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <p className="text-[12px] font-medium text-muted-foreground">{label}</p>
        {Icon ? (
          <div className="h-7 w-7 rounded-lg bg-accent grid place-items-center text-primary">
            <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
          </div>
        ) : null}
      </div>
      <p className="text-[1.75rem] font-semibold tabular-nums tracking-tight leading-none">
        {value}
      </p>
      {hint ? (
        <p className="text-[11px] text-muted-foreground mt-2.5 leading-snug">{hint}</p>
      ) : null}
      {accent ? (
        <div className="mt-3.5 h-1 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary"
            style={{ width: accent }}
          />
        </div>
      ) : null}
    </div>
  );
}

export function LoadingShell({ label }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[380px] gap-3 text-muted-foreground">
      <div className="h-10 w-10 rounded-2xl brand-mark grid place-items-center">
        <Loader2 className="h-4 w-4 text-white animate-spin" />
      </div>
      {label ? <span className="text-xs">{label}</span> : null}
    </div>
  );
}

export function SectionCard({ title, action, children, className = '', padded = true }) {
  return (
    <div className={`surface overflow-hidden ${className}`}>
      {(title || action) && (
        <div className="flex items-center justify-between gap-2 min-h-12 px-4 border-b border-border/80">
          {title ? (
            <div className="text-sm font-semibold tracking-tight truncate">{title}</div>
          ) : (
            <span />
          )}
          {action}
        </div>
      )}
      <div className={padded ? 'p-4' : ''}>{children}</div>
    </div>
  );
}
